import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { setAuthState } from "redux/auth/actions";
import { setUserData } from "redux/user/actions";
import { setActiveStudyId } from "redux/study/actions";

import { TextField } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useNavigate } from "react-router-dom";
import { login } from "util/auth";
import "./index.css";
import act_global_logo from "assets/act_global_logo.png";
import ctms_logo from "assets/ctms_logo.png";
import styles from "./index.module.css";
import router from "routes";
import { useStartTimer } from "timer";
import { post_raw } from "util/requests";

const error_message = "Sorry, your login was not correct.";

const logo = window.location.hostname.includes("actglobaltrial.com") ? act_global_logo : ctms_logo;

export default function SignIn() {
  const [is_loading, setIsLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [showEmail2fa, setShowEmail2fa] = useState(false);
  const [showOTP2fa, setShowOTP2fa] = useState(false);
  const [email, setEmail] = useState("");
  const [_2faCode, set2faCode] = useState("");
  const [googleError, setGoogleError] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const start_timer = useStartTimer();

  useEffect(() => {
    window.onfocus = null;
  }, []);

  const handleLogin = async (force_email = false) => {
    if (is_loading) return;
    if (!password) return;
    setIsLoading(true);
    if (_2faCode && _2faCode.length !== 6) {
      setGoogleError("Code is the wrong length.");
      setIsLoading(false);
      return;
    }
    let type = null;
    if (showEmail2fa || force_email) {
      type = "email";
    } else if (showOTP2fa) {
      type = "otp";
    }

    let two_f_a = _2faCode;
    if (force_email) {
      two_f_a = null;
    }
    function generateSecureRandomString(length) {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      let result = "";
      // Create a Uint8Array array of the specified length
      const randomArray = new Uint8Array(length);

      // Fill the array with cryptographically secure random values
      crypto.getRandomValues(randomArray);

      // Use the random values to select characters from the 'chars' string
      randomArray.forEach((number) => {
        result += chars[number % chars.length];
      });

      return result;
    }

    const array = new Uint8Array(128);
    const code_verifier = generateSecureRandomString(128);

    const msgBuffer = new TextEncoder().encode(code_verifier);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const code_challenge = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");

    login(email, password, two_f_a || null, type, code_challenge)
      .then((ret) => {
        if (ret.data.enable_2fa) {
          if (ret.data.email_2fa) {
            setShowEmail2fa(true);
          } else if (ret.data.google_2fa) {
            setShowOTP2fa(true);
          }
          setIsLoading(false);
        } else {
          post_raw("/auth/token", {
            code: ret.data.authorization_code,
            email,
            code_verifier,
            code: ret.data.authorization_code,
          })
            .then((ret) => {
              dispatch(setAuthState(true));
              // if (ret.data.user_data) {
              //   dispatch(setUserData(ret.data.user_data));
              // }
              start_timer();
              // setTimeout(
              //   () =>
              //     navigate({
              //       pathname: "/subjects",
              //     }),
              //   100
              // );
              // if (ret.data.user_data?.active_study_id) {
              //   let study_id = ret.data.user_data?.active_study_id;
              //   console.log("study_id", study_id);
              //   if (study_id && ret.data.user_data.permissions?.includes("subjects")) {
              //     dispatch(setActiveStudyId(study_id));
              //     start_timer();
              //     navigate({
              //       pathname: "/subjects",
              //     });
              //   } else if (ret.data.user_data.permissions?.includes("subjects")) {
              //     dispatch(setActiveStudyId(1));
              //     start_timer();
              //     navigate("/subjects");
              //   } else {
              //     dispatch(setActiveStudyId(1));
              //     start_timer();
              //     navigate("/user_profile");
              //   }
              // }
              setIsLoading(false);
            })
            .catch((err) => {
              setErrorMessage(error_message);
              setPassword("");
              setIsLoading(false);
            });
        }
      })
      .catch((err) => {
        setIsLoading(false);
        if (err.response.data && err.response.data.message && err.response.data.message === "attempt_limit") {
          setErrorMessage(
            `Too many failed attempts. Please try again in ${err.response.data.retry || 5} minutes, or reset your password.`
          );
          setPassword("");
          return;
        }

        if (!showEmail2fa && !showOTP2fa) {
          setPassword("");
        } else {
          setGoogleError("Invalid code, please try again.");
        }
        setErrorMessage(error_message);
      });
  };

  const handleCancel2fa = () => {
    setShowEmail2fa(false);
    setShowOTP2fa(false);
    setErrorMessage("");
    setPassword("");
    set2faCode(null);
    setGoogleError(null);
  };

  const handleFieldChange = (key) => (e) => {
    if (key === "email") setEmail(e.target.value);
    else if (key === "password") setPassword(e.target.value);
    setErrorMessage(null);
  };

  const onKeyDown = (key) => {
    if (key.key === "Enter") {
      handleLogin();
    }
  };

  const onKeyDown2fa = (key) => {
    if (key.key === "Enter") {
      handleLogin();
    }
  };

  const fallBacktoEmail2fa = () => {
    set2faCode(null);
    setShowOTP2fa(false);
    setGoogleError("");
    setErrorMessage("");
    handleLogin(true);
  };

  return (
    <div className={styles.wrapper}>
      {!showEmail2fa && !showOTP2fa ? (
        <div className={styles.login_card}>
          <img src={logo} width="400px" className="logo" />
          <div className="margin-b-1">
            <TextField
              label="Email"
              variant="standard"
              onChange={handleFieldChange("email")}
              onKeyDown={onKeyDown}
              inputProps={{ style: { textTransform: "lowercase" } }}
            />
          </div>
          <div className="margin-b-2">
            <TextField
              id="standard-basic"
              label="Password"
              variant="standard"
              type="password"
              onChange={handleFieldChange("password")}
              onKeyDown={onKeyDown}
              value={password}
            />
          </div>
          <LoadingButton variant="contained" loading={is_loading} onClick={() => handleLogin(false)}>
            SIGN IN
          </LoadingButton>
          {errorMessage ? <div className={styles.error_message}>{errorMessage}</div> : null}
          <div className={styles.forgot_password} onClick={() => router.navigate("/password_reset_sent")}>
            Forgot password?
          </div>
          {window.location.hostname.includes("migration.platform") ? (
            <div className={styles.staging_text}>(MIGRATION STAGING)</div>
          ) : null}
          {window.location.hostname.includes("test.platform") ? (
            <div className={styles.staging_text}>(STAGING)</div>
          ) : null}
          {window.location.hostname.includes("localhost") || window.location.port.includes("3000") ? (
            <div className={styles.staging_text}>(DEV)</div>
          ) : null}
          {window.location.hostname.includes("training.platform") ? (
            <div className={styles.staging_text}>(TRAINING)</div>
          ) : null}
          {window.location.hostname.includes("qa.platform") ? (
            <div className={styles.staging_text}>(QUALITY ASSURANCE)</div>
          ) : null}
          {window.location.hostname.includes("plabs") ? <div className={styles.staging_text}>(PLABS)</div> : null}
        </div>
      ) : null}
      {showEmail2fa ? (
        <div className={styles.login_card}>
          <img src={logo} className="logo" />
          {/*<div className={styles._2fa_message}>
            Please enter your One-Time-Passcode code.
          </div>*/}
          <div className={styles._2fa_message}>Please enter the 6-digit code you were just emailed.</div>
          <div>
            <TextField
              placeholder=""
              value={_2faCode}
              onChange={(e) => set2faCode(e.target.value)}
              autoComplete="off"
              onKeyDown={onKeyDown2fa}
              sx={{
                "& .MuiInputBase-input": {
                  padding: "5px",
                  textAlign: "center",
                  width: "100px",
                },
              }}
              inputProps={{ inputMode: "numeric" }}
            />
          </div>
          <div className={styles._2fa_button_wrapper}>
            <LoadingButton
              variant="contained"
              loading={is_loading}
              onClick={() => handleLogin(false)}
              className={styles._2fa_button}
            >
              SUBMIT
            </LoadingButton>
            {googleError ? <div className={styles._2fa_error}>{googleError}</div> : null}
          </div>
          <div className={styles.cancel_button} onClick={handleCancel2fa}>
            Cancel
          </div>
        </div>
      ) : null}
      {showOTP2fa ? (
        <div className={styles.login_card}>
          <img src={logo} className="logo" />
          <div className={styles._2fa_message}>
            Please enter your One-Time-Passcode code.
            <br />
            (authenticator app)
          </div>

          <div>
            <TextField
              placeholder=""
              value={_2faCode}
              onChange={(e) => {
                set2faCode(e.target.value);
                setGoogleError(false);
                setErrorMessage(false);
              }}
              autoComplete="off"
              onKeyDown={onKeyDown2fa}
              sx={{
                "& .MuiInputBase-input": {
                  padding: "5px",
                  textAlign: "center",
                  width: "100px",
                },
              }}
              inputProps={{ inputMode: "numeric" }}
            />
          </div>
          <div className={styles._2fa_button_wrapper}>
            <LoadingButton
              variant="contained"
              loading={is_loading}
              onClick={() => handleLogin(false)}
              className={styles._2fa_button}
            >
              SUBMIT
            </LoadingButton>
            {googleError ? <div className={styles._2fa_error}>{googleError}</div> : null}
            {errorMessage && !googleError ? <div className={styles.error_message}>{errorMessage}</div> : null}
          </div>
          <div className={styles.try_another} onClick={fallBacktoEmail2fa}>
            Try another way
          </div>
          <div className={styles.cancel_button} onClick={handleCancel2fa}>
            Cancel
          </div>
        </div>
      ) : null}
      <div className={styles.help_text}>
        For immediate medical or enrollment related questions, please call:
        <br />
        Canada: 1-888-525-6985
      </div>
    </div>
  );
}
